/**
 * Generate a human-friendly date header from metadata.
 * Used as the top-level header above all cards.
 *
 * NOTE: metadata.date_range is an ARRAY ["2026-03-11", "2026-03-15"], not an object.
 *
 * For single_day queries, we use the first event's day_label (pre-computed by backend).
 * For multi_day queries, we show a range header.
 *
 * @param {Object} metadata - raw_output.metadata
 * @param {Array} events - raw_output.events (to read day_label from first event)
 */
export function getDateHeader(metadata, events) {
  // For single-day: use backend's pre-computed day_label from first event
  // Backend computes this dynamically:
  //   today    -> "Today, Mar 11"
  //   tomorrow -> "Tomorrow, Mar 12"
  //   friday   -> "Friday, Mar 13"
  //   any date -> "Wednesday, Mar 18"
  if (metadata?.query_type === 'single_day' && events?.[0]?.day_label) {
    return events[0].day_label;
  }

  // For multi-day: prefer the user's typed phrase from the backend
  // (e.g. "this month", "this week", "next month") so the web header matches
  // the SMS phrasing. Fall back to the raw date range if no phrase was sent.
  const whenPhrase = metadata?.when_phrase;
  if (typeof whenPhrase === 'string' && whenPhrase.trim()) {
    const trimmed = whenPhrase.trim();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }

  const dateRange = metadata?.date_range;
  if (Array.isArray(dateRange) && dateRange.length >= 2) {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formatDate = (dateStr) => {
      const d = new Date(dateStr + 'T00:00:00');
      return `${monthNames[d.getMonth()]} ${d.getDate()}`;
    };
    return `${formatDate(dateRange[0])} \u2013 ${formatDate(dateRange[1])}`;
  }

  return 'Your Schedule';
}

/**
 * Group events by day_label (pre-computed by backend), preserving order.
 * For multi-day queries, events are grouped under their day_label header.
 * For single-day queries, all events share the same day_label.
 *
 * Returns Map<string, event[]> where key is day_label (e.g., "Today, Mar 12").
 */
export function groupEventsByDay(events) {
  const groups = new Map();
  for (const event of events) {
    const dayKey = event.day_label || 'Events';
    if (!groups.has(dayKey)) {
      groups.set(dayKey, []);
    }
    groups.get(dayKey).push(event);
  }
  return groups;
}

/**
 * Group events by calendar_name (Google account email).
 * Returns Map<string, event[]>.
 * If only 1 account, the account header is hidden in the UI.
 */
export function groupEventsByAccount(events) {
  const groups = new Map();
  for (const event of events) {
    const account = event.calendar_name || 'Calendar';
    if (!groups.has(account)) {
      groups.set(account, []);
    }
    groups.get(account).push(event);
  }
  return groups;
}

/**
 * Get CSS variant class (cycles 1-4).
 * @param {number} index - 0-based event index
 * @returns {string} e.g. "variant-1"
 */
export function getVariantClass(index) {
  return `variant-${(index % 4) + 1}`;
}

/**
 * Get illustration name (cycles through 8 types).
 * @param {number} index - 0-based event index
 * @returns {string} Illustration component name
 */
export function getIllustrationName(index) {
  const illustrations = [
    'strategy', 'campaign', 'team', 'design',
    'oneOnOne', 'allHands', 'training', 'abstract'
  ];
  return illustrations[index % illustrations.length];
}

/**
 * Strip the " - Kiln <Location> - " segment from a calendar event location.
 *
 * Google Calendar locations sourced from OfficeRND come in the form:
 *   "Cactus Conference Room - Kiln Boulder - 2101 Pearl St, Boulder, CO 80302, USA"
 *
 * The middle "Kiln Boulder" segment duplicates info already in the street
 * address, so we drop it and join the remaining parts with a middle dot
 * (matches the existing card language used in the time row):
 *   "Cactus Conference Room · 2101 Pearl St, Boulder, CO 80302, USA"
 *
 * If the pattern isn't present, the location is returned untouched.
 *
 * @param {string} location
 * @returns {string}
 */
export function formatLocation(location) {
  if (!location || typeof location !== 'string') return location || '';
  return location.replace(/\s*-\s*Kiln\s+[^-]+?\s*-\s*/i, ' · ').trim();
}

/**
 * Same cleanup as formatLocation, but returns the room name and the
 * street-address portion split into two pieces so the card can render
 * them on separate lines when the pill wraps.
 *
 * Returns { roomPart, addressPart } where addressPart is null if the
 * cleaned string has no separator (single-piece location).
 *
 * @param {string} location
 * @returns {{ roomPart: string, addressPart: string|null } | null}
 */
export function formatLocationParts(location) {
  const cleaned = formatLocation(location);
  if (!cleaned) return null;
  const sepIndex = cleaned.indexOf(' · ');
  if (sepIndex === -1) {
    return { roomPart: cleaned, addressPart: null };
  }
  return {
    roomPart: cleaned.slice(0, sepIndex).trim(),
    addressPart: cleaned.slice(sepIndex + 3).trim(),
  };
}

/**
 * Detect video conferencing platform from event data.
 * @returns {'zoom'|'meet'|'teams'|null}
 */
export function detectPlatform(event) {
  // Prefer explicit field
  if (event.meeting_platform) {
    const platform = event.meeting_platform.toLowerCase();
    if (platform.includes('zoom')) return 'zoom';
    if (platform.includes('meet')) return 'meet';
    if (platform.includes('teams')) return 'teams';
  }

  // Fallback: parse conference_link URL
  const link = event.conference_link || '';
  if (link.includes('zoom.us')) return 'zoom';
  if (link.includes('meet.google.com')) return 'meet';
  if (link.includes('teams.microsoft.com') || link.includes('teams.live.com')) return 'teams';

  return null;
}
